import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Cookie helpers for cross-domain sync
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]*)'));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; domain=.cikastudio.com; path=/; max-age=31536000; SameSite=Lax`;
}

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'cikastudio-theme';

type ThemeContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

function getInitialTheme(): Theme {
  // 1. Read cookie (user's previous choice, cross-domain)
  const saved = getCookie(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  
  // 2. Follow system
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());

  // 同步到其他标签页
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setCookie(STORAGE_KEY, newTheme);
    
    // Apply immediately
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [theme, setTheme]);

  // Initial mount: sync class to DOM (FOUC script may have set it, ensure React state matches)
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []); // run once on mount

  // Cross-tab sync via cookie polling
  useEffect(() => {
    const interval = setInterval(() => {
      const cookieTheme = getCookie(STORAGE_KEY) as Theme;
      if (cookieTheme && cookieTheme !== theme && (cookieTheme === 'light' || cookieTheme === 'dark')) {
        setThemeState(cookieTheme);
        const root = document.documentElement;
        if (cookieTheme === 'dark') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
